<template>
  <FlashSalesTag
    v-if="dealContent"
    :icon="dealContent.icon"
    :label="dealContent.label"
    :tooltip-text="dealContent.tooltipText"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Deal } from '@backmarket/nuxt-layer-cms/products/algolia/types'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { IconArrowDownRight } from '@ds/icons/IconArrowDownRight'
import { IconBoltFilled } from '@ds/icons/IconBoltFilled'

import type { Granularity } from '../models/product'

import FlashSalesTag from './FlashSales/components/FlashSalesTag.vue'
import translations from './ProductCardDeal.translations'

const i18n = useI18n()
const experiments = useExperiments()
const isFlashSalesEnabled = experiments['experiment.flashSales'] === 'enabled'
const isSellerFlashSalesEnabled =
  experiments['experiment.sellerFlashSales'] === 'with'
const locale = useI18nLocale()
const { deal, granularity } = defineProps<{
  deal?: Deal
  granularity?: Granularity
}>()

const dealContent = computed(() => {
  if (!deal) return null
  if (isFlashSalesEnabled && deal.type === 'flash_sales')
    return {
      label: i18n(translations.flashSalesTag, {
        price: i18n.price(deal.discount, {
          trailingZeroDisplay: 'stripIfInteger',
        }),
      }),
      icon: IconBoltFilled,
    }
  if (
    isSellerFlashSalesEnabled &&
    granularity === 'backbox' &&
    deal.type === 'seller_flash_sales' &&
    deal.percentage?.value
  ) {
    const now = new Date()
    const day = now.getDay()

    const mondayThisWeek = new Date(now)
    mondayThisWeek.setDate(now.getDate() - ((day + 6) % 7))
    mondayThisWeek.setHours(12, 0, 0, 0)

    // Monday 12pm before
    const startDate =
      day === 1 && now.getHours() < 12
        ? new Date(mondayThisWeek.getTime() - 7 * 24 * 60 * 60 * 1000)
        : mondayThisWeek

    // Monday 12pm after
    const endDate = new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000)

    return {
      label: i18n(translations.sellerFlashSalesTag, {
        percentage: `${deal.percentage.value}%`,
      }),
      icon: IconArrowDownRight,
      tooltipText: i18n(translations.sellerFlashSalesTagTooltip, {
        start_date: startDate.toLocaleDateString(locale, {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
        }),
        end_date: endDate.toLocaleDateString(locale, {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
        }),
        percentage: deal.percentage.value,
        lowest_price: i18n.price(deal.percentage.minimum_price, {
          trailingZeroDisplay: 'stripIfInteger',
        }),
      }),
    }
  }

  return null
})
</script>
